/**
 * Transforms technical error messages into user-friendly descriptions.
 * Used for toast notifications and inline error displays.
 */

type ErrorPattern = {
  pattern: RegExp | string;
  message: string;
};

const ERROR_PATTERNS: ErrorPattern[] = [
  // Edge function and network errors
  { pattern: /edge function returned (?:a )?non-2\d{2}/i, message: "We're having trouble connecting to the server. Please try again." },
  { pattern: /failed to fetch/i, message: "Could not connect to the server. Check your internet connection and try again." },
  { pattern: /network error/i, message: "Network connection failed. Please check your internet and try again." },
  { pattern: /timeout/i, message: "The request took too long. Please try again." },

  // Auth and permission errors
  { pattern: /jwt|token.*expired|session.*expired/i, message: "Your session has expired. Please sign in again." },
  { pattern: /unauthorized|not authenticated/i, message: "You need to sign in to do this." },
  { pattern: /forbidden|permission denied|not authorized/i, message: "You don't have permission to do this." },
  { pattern: /row-level security|insufficient privilege|42501/i, message: "You don't have permission to access this data." },
  { pattern: /PGRST116/i, message: "The operation didn't return a result. You may not have permission to perform this action." },
  { pattern: /JSON object requested, return was empty/i, message: "The operation didn't return a result. You may not have permission to perform this action." },

  // Database constraint errors
  { pattern: /duplicate key|unique constraint|23505/i, message: "This record already exists. Please use a different value." },
  { pattern: /foreign key|23503/i, message: "This action can't be completed because it references missing data." },
  { pattern: /invalid.*input|22P02/i, message: "Some of the information provided isn't in the right format." },
  { pattern: /dependencies changed; reload/i, message: "Dependencies changed elsewhere. Please reopen the editor and try again." },

  // Validation errors
  { pattern: /required field|missing required/i, message: "Please fill in all required fields." },
  { pattern: /invalid email/i, message: "Please enter a valid email address." },
  { pattern: /invalid phone/i, message: "Please enter a valid phone number." },

  // Rate limiting
  { pattern: /rate limit|too many requests|429/i, message: "You're doing that too fast. Please wait a moment and try again." },

  // Server errors (use word boundaries to avoid false matches on e.g. "$500")
  { pattern: /internal server error/i, message: "Something went wrong on our end. Please try again in a moment." },
  { pattern: /service unavailable/i, message: "The service is temporarily unavailable. Please try again shortly." },
  { pattern: /bad gateway/i, message: "We're having trouble connecting. Please try again." },
];

/**
 * Default fallback message when no pattern matches.
 */
const DEFAULT_MESSAGE = "Something went wrong. Please try again.";

/**
 * Checks if an error message matches any known technical pattern.
 */
function matchErrorPattern(message: string): string | null {
  for (const { pattern, message: friendlyMessage } of ERROR_PATTERNS) {
    if (typeof pattern === "string") {
      if (message.toLowerCase().includes(pattern.toLowerCase())) {
        return friendlyMessage;
      }
    } else if (pattern.test(message)) {
      return friendlyMessage;
    }
  }
  return null;
}

/**
 * Checks if a message appears to be technical jargon that shouldn't be shown to users.
 *
 * Uses precise patterns that avoid false positives on legitimate backend error
 * messages (e.g., Stripe API errors that mention "function", "API", etc. in a
 * user-relevant context).
 */
function isTechnicalMessage(message: string): boolean {
  const technicalIndicators = [
    /\bPGRST\d+\b/,          // PostgREST error codes like PGRST204
    /\bSQL\b/,                // SQL-specific context
    /\bHTTP\s*\d{3}\b/,       // "HTTP 500" style messages
    /\bUUID\b/,               // UUID references
    /\bRPC\b/,                // RPC call references
    /\bJWT\b/,                // JWT token references
    /\b(column|table|schema|constraint)\b/i,  // DB schema terms
    /\bquery\s+(failed|error|timeout)/i,      // query failure messages
    /^\[.*?\]/,               // Messages starting with [Context] like [EdgeFunction:name]
    /\bstatus\s*(?:code\s*)?\d{3}\b/i,        // "status 500" or "status code 403"
    /\berror code\b/i,
    /\bstack trace\b/i,
    /\bfunction\s+(?:invoke|call|returned|error)/i,  // edge function technical messages only
  ];
  return technicalIndicators.some(pattern => pattern.test(message));
}

/**
 * Transforms a technical error message into a user-friendly message.
 *
 * @param error - The error object or message
 * @param fallback - Optional custom fallback message if no pattern matches
 * @returns A user-friendly error message
 *
 * @example
 * const message = getUserFriendlyError(error);
 * toast.error("Failed to save", { description: message });
 */
export function getUserFriendlyError(
  error: unknown,
  fallback: string = DEFAULT_MESSAGE
): string {
  if (!error) return fallback;

  // Extract message from various error formats
  let message: string;
  if (typeof error === "string") {
    message = error;
  } else if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === "object" && error !== null) {
    const errorObj = error as { message?: string; error?: string; description?: string };
    message = errorObj.message ?? errorObj.error ?? errorObj.description ?? String(error);
  } else {
    message = String(error);
  }

  if (!message || message === "[object Object]") {
    return fallback;
  }

  // Check for known patterns
  const friendlyMessage = matchErrorPattern(message);
  if (friendlyMessage) {
    return friendlyMessage;
  }

  // If the message looks technical, use the fallback
  if (isTechnicalMessage(message)) {
    return fallback;
  }

  // If it's a simple, readable message, return it as-is
  // (allows custom error messages from the backend to pass through)
  return message;
}

/**
 * Creates a user-friendly error description for toast notifications.
 * Returns undefined if the error doesn't warrant a description.
 *
 * @param error - The error object or message
 * @returns A user-friendly description or undefined
 */
export function getErrorDescription(error: unknown): string | undefined {
  const message = getUserFriendlyError(error);
  // Don't return the default message as a description - it's redundant
  if (message === DEFAULT_MESSAGE) {
    return undefined;
  }
  return message;
}
